import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LargeTitle, StatCard, Surface, Skeleton, Pill, AButton, EmptyState } from "@/components/ios";
import {
  Home, AlertTriangle, ShieldAlert, Sparkles, Clock, TrendingUp, ClipboardCheck, ChevronRight, Plus,
} from "lucide-react";
import { motion } from "framer-motion";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [due, setDue] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    api.get("/schedule/due").then(({ data }) => setDue(data));
  }, []);

  if (!stats)
    return (
      <div>
        <LargeTitle subtitle="Loading your portfolio…">Overview</LargeTitle>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );

  return (
    <div>
      <LargeTitle subtitle="Your portfolio at a glance"
        right={<AButton testid="new-inspection-dash-header" onClick={() => nav("/properties")}><Plus size={15} className="inline mr-1 -mt-0.5" /> New inspection</AButton>}>
        Overview
      </LargeTitle>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-properties" icon={Home} label="Properties" value={stats.properties} hint="Under management" />
        <StatCard testid="stat-due" icon={Clock} label="Inspections due" value={stats.due_soon} tone="warning" hint="Within 14 days" />
        <StatCard testid="stat-overdue" icon={AlertTriangle} label="Overdue" value={stats.overdue} tone="danger" hint="Action required" />
        <StatCard testid="stat-compliance" icon={ShieldAlert} label="Compliance gaps" value={stats.compliance_gaps} tone="danger" hint={`${stats.compliance_due_soon} due soon`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        {/* ROI highlight */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2">
          <Surface className="p-6 h-full relative overflow-hidden">
            <div className="flex items-center gap-2 text-sm font-medium text-secondary">
              <TrendingUp size={16} className="accent-text" /> Time saved by AI drafting
            </div>
            <div className="mt-3 flex items-end gap-3">
              <span className="text-5xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
                {stats.time_saved_hours}
              </span>
              <span className="text-lg text-secondary mb-1.5">hours</span>
            </div>
            <p className="text-sm text-secondary mt-2 max-w-md">
              Estimated agent time saved across {stats.reports_total} report{stats.reports_total === 1 ? "" : "s"} vs. manual
              transcription — roughly 35 minutes per inspection returned to your team.
            </p>
            <div className="mt-5 flex flex-wrap gap-4">
              <div>
                <div className="text-xs text-secondary">AI drafts kept as-is</div>
                <div className="text-xl font-semibold" style={{ color: "var(--text)" }}>
                  {stats.ai_accuracy != null ? `${stats.ai_accuracy}%` : "—"}
                </div>
                <div className="text-[11px] text-secondary mt-0.5">of AI condition ratings the agent accepted without change</div>
              </div>
              <div>
                <div className="text-xs text-secondary">Items confirmed</div>
                <div className="text-xl font-semibold" style={{ color: "var(--text)" }}>{stats.confirmed}</div>
              </div>
              <div>
                <div className="text-xs text-secondary">Corrections logged</div>
                <div className="text-xl font-semibold" style={{ color: "var(--text)" }}>{stats.corrections}</div>
              </div>
            </div>
          </Surface>
        </motion.div>

        <Surface className="p-6">
          <div className="flex items-center gap-2 text-sm font-medium text-secondary">
            <Sparkles size={16} className="accent-text" /> Per-PM workload
          </div>
          <div className="mt-4 space-y-3">
            {stats.workload.map((w) => (
              <div key={w.name}>
                <div className="flex justify-between text-sm" style={{ color: "var(--text)" }}>
                  <span>{w.name}</span>
                  <span className="font-semibold">{w.properties}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full" style={{ background: "var(--surface-2)" }}>
                  <div className="h-full rounded-full accent-bg" style={{ width: `${Math.min(100, (w.properties / stats.properties) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <AButton testid="new-inspection-dash" className="w-full mt-6" onClick={() => nav("/inspections")}>
            Start an inspection
          </AButton>
        </Surface>
      </div>

      <h2 className="text-xl font-semibold tracking-tight mt-8 mb-3" style={{ color: "var(--text)" }}>Attention needed</h2>
      {due.length === 0 ? (
        <Surface><EmptyState icon={ClipboardCheck} title="All caught up" subtitle="No inspections are due or overdue right now." /></Surface>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {due.map((p) => (
            <Surface key={p.id} className="p-4 cursor-pointer active:scale-[0.99] transition-transform" onClick={() => nav(`/properties/${p.id}`)}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold" style={{ color: "var(--text)" }}>{p.address}</div>
                  <div className="text-sm text-secondary">{p.suburb}, {p.state}</div>
                </div>
                <Pill tone={p.due_status === "overdue" ? "danger" : "warning"}>
                  {p.due_status === "overdue" ? "Overdue" : "Due soon"}
                </Pill>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-secondary">
                <span>{p.beds} bed · {p.baths} bath · {p.type}</span>
                <ChevronRight size={16} />
              </div>
            </Surface>
          ))}
        </div>
      )}
    </div>
  );
}
